import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Smartphone, Wallet, Luggage } from "lucide-react";
import { TRAVEL_CARD } from "@/data/travelCard";
import { useCountry } from "@/context/CountryContext";

/**
 * Home-page teaser for the Travel Card product. Sits just below the
 * TheftUrgency panel — positions it as a complement to magnetic trackers
 * (wallet / keys / luggage, not vehicle / tool).
 *
 * Keep the copy ruthlessly benefit-led and compact. One CTA: "Explore Travel Card".
 */
const TravelCardTeaser = () => {
  const { currency, symbol } = useCountry();
  const price = TRAVEL_CARD.priceByCurrency[currency] || TRAVEL_CARD.priceByCurrency.AUD;
  const compareAt = TRAVEL_CARD.compareAtByCurrency[currency] || TRAVEL_CARD.compareAtByCurrency.AUD;

  return (
    <section
      data-testid="travel-card-teaser"
      className="relative overflow-hidden bg-gradient-to-br from-tracksy-bone via-white to-tracksy-bone py-16 sm:py-24"
    >
      {/* decorative accent */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 flex justify-center opacity-[0.05]">
        <div className="h-96 w-96 rounded-full bg-tracksy-orange blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        {/* COPY */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-tracksy-ink/[0.06] px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-tracksy-ink ring-1 ring-tracksy-ink/10">
            New · Dual-Ecosystem
          </div>
          <h2 className="mt-5 font-heading text-3xl font-black leading-[1.05] tracking-tight text-tracksy-ink sm:text-4xl lg:text-5xl">
            One card. Every phone.
            <br />
            <span className="text-tracksy-orange">Never lost again.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-tracksy-ink/70 sm:text-lg">
            The <strong>Tracksy Travel Card</strong> is the only tracker that talks to both <strong>Apple Find My</strong> and the new <strong>Google Find Hub</strong> — natively. Slip it in a wallet, bag or your kid's phone case. 2.0mm thin. Fully rechargeable.
          </p>

          {/* Use-case strip */}
          <ul className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: Wallet, label: "Wallets" },
              { icon: Luggage, label: "Luggage" },
              { icon: Smartphone, label: "Kids' phones" },
            ].map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-tracksy-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-tracksy-ink"
              >
                <Icon className="h-3.5 w-3.5 text-tracksy-orange" />
                {label}
              </li>
            ))}
          </ul>

          {/* Price + CTA */}
          <div className="mt-8 flex flex-wrap items-end gap-4">
            <div className="flex items-end gap-2">
              <span className="font-heading text-4xl font-black text-tracksy-ink">
                {symbol}{price}
              </span>
              <span className="pb-1.5 text-sm font-semibold text-tracksy-ink/40 line-through">
                {symbol}{compareAt}
              </span>
              <span className="pb-1.5 text-xs font-black uppercase tracking-wider text-tracksy-green">
                Save {symbol}{compareAt - price}
              </span>
            </div>
          </div>

          <Link
            data-testid="travel-card-teaser-cta"
            to="/travel-card"
            className="group mt-5 inline-flex items-center gap-2 rounded-full bg-tracksy-ink px-6 py-3.5 text-sm font-black uppercase tracking-wider text-white transition hover:bg-tracksy-orange"
          >
            Explore the Travel Card
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>

          {/* Bundle hint */}
          <p className="mt-5 text-xs font-semibold text-tracksy-ink/60">
            🎁 Also included free with every 4-Pack and 8-Pack of magnetic Tracksys.
          </p>
        </motion.div>

        {/* IMAGE — floating card, isolated via multiply blend so the baked-in
            white background of the PNG disappears into the page cream. */}
        <motion.div
          initial={{ opacity: 0, x: 24, rotate: 6 }}
          whileInView={{ opacity: 1, x: 0, rotate: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, type: "spring", stiffness: 160, damping: 22 }}
          className="relative mx-auto flex items-center justify-center"
        >
          {/* Soft elliptical floor shadow — see TravelCard.jsx for rationale. */}
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-2 left-1/2 h-6 w-52 -translate-x-1/2 rounded-[50%] bg-black/25 blur-2xl sm:h-7 sm:w-64 lg:w-72"
          />
          <motion.img
            src={TRAVEL_CARD.heroImage}
            alt="Tracksy Travel Card — dual-ecosystem Bluetooth tracker"
            loading="lazy"
            className="relative h-56 w-auto object-contain sm:h-72 lg:h-80"
            style={{ mixBlendMode: "multiply" }}
            animate={{ y: [0, -8, 0], rotate: [0, 2, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="pointer-events-none absolute -bottom-2 flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-widest text-tracksy-ink/40">
            <span>Apple Find My</span>
            <span className="h-0.5 w-6 bg-tracksy-ink/15" />
            <span>Google Find Hub</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TravelCardTeaser;
